import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Ask the Cook chat drawer — portaled to document.body.
 * Handles desktop anchor alignment, mobile keyboard inset, and swipe-to-dismiss.
 */
export default function ChatDrawer({
  open,
  onClose,
  isMobileViewport,
  anchorRef,
  onOpenHelp,
  onClearChat,
  showClearChat,
  children,
}) {
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [desktopAnchorLeft, setDesktopAnchorLeft] = useState(null)
  const swipeRef = useRef({ startX: 0, startY: 0, startTime: 0, currentOffset: 0 })

  useEffect(() => {
    if (!open) return
    setDragOffset(0)
    setIsDragging(false)
    swipeRef.current = { startX: 0, startY: 0, startTime: 0, currentOffset: 0 }
  }, [open])

  useEffect(() => {
    if (!open || !isMobileViewport) return undefined
    const vv = window.visualViewport
    if (!vv) return undefined
    const drawer = document.getElementById('chat-drawer')
    if (!drawer) return undefined

    function syncKeyboardInset() {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      drawer.style.setProperty('--chat-drawer-keyboard-inset', inset > 0 ? `${inset}px` : '0px')
    }

    syncKeyboardInset()
    vv.addEventListener('resize', syncKeyboardInset)
    vv.addEventListener('scroll', syncKeyboardInset)
    return () => {
      vv.removeEventListener('resize', syncKeyboardInset)
      vv.removeEventListener('scroll', syncKeyboardInset)
      drawer.style.removeProperty('--chat-drawer-keyboard-inset')
    }
  }, [open, isMobileViewport])

  useEffect(() => {
    if (!open || isMobileViewport) {
      setDesktopAnchorLeft(null)
      return undefined
    }

    function syncDesktopLayout() {
      const anchor = anchorRef?.current
      if (!anchor) return
      setDesktopAnchorLeft(anchor.getBoundingClientRect().left)
    }

    syncDesktopLayout()
    window.addEventListener('resize', syncDesktopLayout)
    window.addEventListener('scroll', syncDesktopLayout, true)
    return () => {
      window.removeEventListener('resize', syncDesktopLayout)
      window.removeEventListener('scroll', syncDesktopLayout, true)
    }
  }, [open, isMobileViewport, anchorRef])

  function handleTouchStart(e) {
    const touch = e.touches[0]
    if (!touch) return
    swipeRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      currentOffset: 0,
    }
  }

  function handleTouchMove(e) {
    const touch = e.touches[0]
    if (!touch || swipeRef.current.startTime == null) return

    if (isMobileViewport) {
      const dy = touch.clientY - swipeRef.current.startY
      if (!isDragging && dy > 8) setIsDragging(true)
      if (isDragging && dy > 0) {
        e.preventDefault()
        swipeRef.current.currentOffset = dy
        setDragOffset(dy)
      }
      return
    }

    const dx = touch.clientX - swipeRef.current.startX
    if (!isDragging && Math.abs(dx) > 8) setIsDragging(true)
    if (isDragging) {
      e.preventDefault()
      const offset = Math.max(0, dx)
      swipeRef.current.currentOffset = offset
      setDragOffset(offset)
    }
  }

  function handleTouchEnd() {
    const { startTime, currentOffset } = swipeRef.current
    setIsDragging(false)
    const threshold = 80
    const elapsed = Date.now() - startTime
    const velocity = elapsed > 0 ? currentOffset / elapsed : 0
    if (currentOffset >= threshold || velocity >= 0.25) {
      onClose()
      setDragOffset(0)
    } else {
      setDragOffset(0)
    }
    swipeRef.current = { startX: 0, startY: 0, startTime: 0, currentOffset: 0 }
  }

  if (!open || typeof document === 'undefined') return null

  const cssVars = {}
  if (!isMobileViewport && desktopAnchorLeft != null) {
    cssVars['--chat-drawer-anchor-left'] = `${desktopAnchorLeft}px`
  }
  if (dragOffset) {
    cssVars['--chat-drawer-drag-offset'] = `${dragOffset}px`
  }

  return createPortal(
    <>
      <div
        id="chat-drawer-backdrop"
        className={`chat-drawer-backdrop${isMobileViewport ? ' chat-drawer-backdrop--mobile-sheet' : ' chat-drawer-backdrop--desktop-overlay'}`}
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close chat"
      />
      <div
        id="chat-drawer"
        className={[
          'chat-drawer',
          isMobileViewport ? 'chat-drawer--mobile-sheet' : 'chat-drawer--desktop-overlay',
          !isMobileViewport && desktopAnchorLeft != null ? 'chat-drawer--anchored' : '',
          isDragging ? 'chat-drawer-dragging' : '',
        ].filter(Boolean).join(' ')}
        role="dialog"
        aria-label="Ask the Cook"
        style={Object.keys(cssVars).length > 0 ? cssVars : undefined}
      >
        <div
          className="chat-drawer-drag-region"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="chat-drawer-handle" aria-hidden="true" role="presentation">
            <span className="chat-drawer-handle-pill" />
          </div>
        </div>
        <header className="chat-drawer-panel-header">
          <div className="chat-drawer-panel-header-left">
            <span className="ask-the-cook-avatar" aria-hidden="true">✦</span>
            <span className="ask-the-cook-panel-title">Ask the Cook</span>
          </div>
          <div className="chat-drawer-panel-header-right">
            {typeof onOpenHelp === 'function' ? (
              <button
                type="button"
                className="btn-icon btn-icon--sm chat-panel-toolbar-btn--help"
                onClick={onOpenHelp}
                aria-label="Help"
                title="Help"
              >
                <i className="fas fa-info-circle" aria-hidden="true" />
              </button>
            ) : null}
            {showClearChat && (
              <button
                type="button"
                className="btn-icon btn-icon--sm chat-panel-toolbar-btn--destructive"
                onClick={onClearChat}
                aria-label="Clear conversation"
                title="Clear conversation"
              >
                <i className="fas fa-trash-alt" aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="btn-icon btn-icon--sm ask-the-cook-dismiss-btn"
              onClick={onClose}
              aria-label="Close Ask the Cook"
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
        </header>
        {children}
      </div>
    </>,
    document.body
  )
}
